<template>
    <div class="product-images-gallery">
        <div class="product-image">
            <div class="sale" v-if="this.onSale">
                SALE
            </div>

            <ssr-carousel ref="carousel"
                          :key="images.length"
                          @change="updateThumnbail($event.index)">
                <div v-for="image in images"
                     :key="image.id"
                     @click="previewImage(originalImage)">
                    <video-player v-if="image.display_type === 'video'"
                                  :src="get(image, 'images_url.video_preview')"/>

                    <nuxt-picture v-else
                                    :alt="get(image, 'alternative_text')"
                                    :src="get(image, 'images_url.preview')"
                                    :preload="isMainImage(image)"
                                    v-fallback-image="$defaultVariantImage"
                                    @click="!isMobile && previewImage(originalImage)"/>
                </div>
            </ssr-carousel>
        </div>

        <div class="product-display" @click="previewImage(originalImage)" v-if="viewLarge">
            <i class="fas fa-search"></i>
            View large
        </div>

        <div v-if="!hideThumbnails" class="product-thumbnails">
            <div v-for="(image, index) in images"
                 class="thumbnail"
                 :class="{ active: index === selectedIndex }"
                 :key="image.id"
                 @click="selectImage(index)">

                <div v-if="image.display_type === 'video'"
                     class="video-icon">
                  <div class="video-icon-triangle"/>
                </div>

                <nuxt-picture width="100"
                              height="100"
                              :alt="get(image, 'alternative_text')"
                              :src="getThumbImageUrl(image)"
                              :key="getThumbImageUrl(image)"
                              loading="lazy"/>
            </div>
        </div>
    </div>
</template>

<script>
    import WindowSizeMixin from '@/mixins/windowSize'
    import { get } from 'lodash'
    import SsrCarousel from 'vue-ssr-carousel'
    import VideoPlayer from 'nuxt-video-player'
    import 'vue-ssr-carousel/index.css'
    import 'nuxt-video-player/src/assets/css/main.css'

    export default {
        name: 'product-images-gallery',
        mixins: [WindowSizeMixin],
        components: {
            SsrCarousel,
            VideoPlayer
        },
        props: {
            images: Array,
            onSale: {
                type: Boolean,
                default: false
            },
            salePrice: {
                type: Number,
                default: 0
            },
            price: {
                type: Number,
                default: 0
            },
            viewLarge: {
                type: Boolean,
                default: true
            },
            hideThumbnails: {
                type: Boolean,
                default: false
            },
            navigationEnabled: {
                type: Boolean,
                default: false
            }
        },
        data () {
            return {
                selectedIndex: 0
            }
        },
        computed: {
            originalImage() {
                return this.get(this.images, `${this.selectedIndex}.images_url.original`, '')
            }
        },
        methods: {
            get: get,
            isMainImage (image) {
                return image.display_type === 'main'
            },
            updateThumnbail (index) {
                this.selectedIndex = index
            },
            selectImage(index) {
                this.updateThumnbail(index)

                this.$nextTick(() => {
                    this.$refs.carousel.goto(index)
                })
            },
            previewImage(url) {
                this.$store.dispatch('imagePreview/showPreview', url)
            },
            getThumbImageUrl ({ display_type, images_url: { thumb, video_thumb } }) {
                return display_type === 'video' ? video_thumb : thumb
            }
        }
    }
</script>

<style lang="scss" scoped>
    @import '../assets/stylesheets/variables';
    @import '../../scout/assets/stylesheets/animations/fade';

    .product-images-gallery {
        display: grid;
        grid-row-gap: 10px;
        text-align: center;

        .ssr-carousel-slide {
            height: 500px;
        }

        .product-image {
            border: 1px solid $color-gray-lighter;
            position: relative;
            height: 500px;
            min-width: 0;
            overflow: hidden;
            box-sizing: border-box;

            .sale {
                background-color: $color-sale;
                position: absolute;
                color: white;
                padding: 4px 16px;
                font-family: OpenSans;
                font-size: 14px;
                font-weight: 100;
                font-style: normal;
                font-stretch: normal;
                line-height: 1.36;
                letter-spacing: 0.7px;
                top: 15px;
                z-index: 15;
            }

            ::v-deep img {
                max-height: 500px;
                object-fit: cover;
                top: 0;
                bottom: 0;
                left: 0;
                right: 0;
                margin: auto;
                max-width: 100%;
                cursor: pointer;
            }
        }

        .product-display {
            font-size: 1.4rem;
            cursor: pointer;
            color: $color-gray-light;
            text-align: left;
        }

        .product-thumbnails {
            display: grid;
            grid-template-columns: repeat(5, 1fr);
            grid-gap: 6px;

            .thumbnail {
                border: 1px solid $color-gray-lighter;
                padding: 4px;
                cursor: pointer;
                text-align: center;
                display: flex;
                align-items: center;
                justify-content: center;
                height: 100px;
                position: relative;

                &.active, &:hover{
                    border-color: #00adee;
                }

                img{
                    position: absolute;
                    max-width: 100%;
                    max-height: 100px;
                }
            }
        }

        .video-icon {
          border: 1px solid $color-black;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: rgba(255, 255, 255, .5);
          border-radius: 100%;
          position: absolute;
          margin: auto;
          left: 0;
          right: 0;
          top: 0;
          bottom: 0;
          width: 50px;
          height: 50px;
          cursor: pointer;
          pointer-events: none;
        }

        .video-icon-triangle {
          width: 0;
          height: 0;
          border-style: solid;
          border-width: 12.5px 0 12.5px 21.7px;
          border-color: transparent transparent transparent $color-black;
          margin-left: 5px;
        }

        @media only screen and (max-width: 600px) {
            ::v-deep .product-image, .ssr-carousel-slide {
                height: 250px;

                img{
                    max-height: 250px;
                }
            }

            .product-thumbnails{
                grid-template-columns: repeat(3, 1fr);

                .thumbnail{
                    height: 80px;

                    img{
                        max-height: 80px;
                    }
                }
            }
        }

        @media only screen and (min-width: 600px) and (max-width: 768px) {
            ::v-deep .product-image, .ssr-carousel-slide {
                height: 250px;

                img{
                    max-height: 250px;
                }
            }

            .product-thumbnails{
                grid-template-columns: repeat(4, 1fr);
            }
        }

        @media only screen and (min-width: 768px) and (max-width: 992px) {
            ::v-deep .product-image, .ssr-carousel-slide {
                height: 400px;

                img{
                    max-height: 400px;
                }
            }
        }
    }
</style>
